import type { users as UsersNS } from '@cwos/api-client';

/**
 * RESERVED IS A THING THE PRODUCT SAYS, NOT A BOX THAT HAPPENS TO BE UNTICKED.
 *
 * ---------------------------------------------------------------------------
 * WHY `isGrantable` IS NOT ENOUGH ON ITS OWN
 * ---------------------------------------------------------------------------
 * `users.isGrantable(meta)` is the correct predicate for whether to OFFER a
 * control, and it is default-deny in exactly the right way: `reserved` is false,
 * a metadata entry with no `grantability` is false, an entry this bundle cannot
 * parse is false. Its own docblock says the rest out loud — *"a screen that
 * wants to SAY WHY should read `grantability` and `reservedReason` directly:
 * `false` here covers both 'reserved' and 'the server did not say', which are
 * different sentences."*
 *
 * This module is that reading, written once. It exists because a permission
 * picker has to do two different things with the same key: refuse to offer it,
 * and explain itself. Collapsing the second into the first produces the two
 * failures this surface must not have —
 *
 *   * an UNTICKED BOX an administrator will keep trying to tick, until the
 *     server refuses the save and they conclude the product is broken; and
 *   * a HIDDEN ROW, which makes `permissionGrantRefusal`'s refusal look like a
 *     bug when the key turns up in an audit row or an error message and cannot
 *     be found on the screen that supposedly lists every permission.
 *
 * The catalog the picker renders comes from `GET /roles/overview`, which sends
 * the live seeded catalog AND `PERMISSION_METADATA` verbatim. So the reserved
 * answer on screen is the server's own field, not a client-side list that would
 * be a second source of authority and would drift from the first.
 *
 * ---------------------------------------------------------------------------
 * WHY IT MATTERS ON THIS DOOR SPECIFICALLY
 * ---------------------------------------------------------------------------
 * `permissionGrantRefusal` guards BOTH the direct-grant door
 * (`POST /users/:id/permissions`, which answers 422 with the key's
 * `reservedReason`) and the composed-role door, through `privilegeCeiling`'s
 * rule 2 — and on the orgchart doors `grantabilityGate` is left at `'apply'`
 * precisely because the keys are caller-supplied there and there is no second
 * gate behind them. Composition is where a reserved key would otherwise be
 * laundered into a person: put it in a role, then assign the role. The server
 * closes that; a picker that hides the key leaves the administrator with no way
 * to learn why.
 */

/**
 * What the product says about handing this key out — FOUR answers, because the
 * reasons differ and so do the sentences.
 */
export type GrantStanding =
  /** An administrator holding `role:assign` may confer it. */
  | 'grantable'
  /**
   * A real key of the real catalog that the product declines to confer by hand.
   * Carries a `reservedReason`; `agents:*` and `task:observe` live here.
   */
  | 'reserved'
  /**
   * The catalog has the key and NOTHING DESCRIBES IT. `permissionGrantRefusal`
   * answers `'unknown'` for this and refuses — correctly, per its own comment:
   * being refused in the window between a migration seeding a key and somebody
   * describing it "is the correct behaviour, not a gap".
   */
  | 'undescribed'
  /**
   * The key is described and the description does not say whether it may be
   * granted. A server older than F418, or a field this bundle does not
   * recognise. NOT `grantable`: `KNOWN_PERMISSION != ORG_ADMIN_GRANTABLE_PERMISSION`.
   */
  | 'unstated';

export function grantStanding(meta: UsersNS.PermissionMeta | undefined): GrantStanding {
  if (meta === undefined) return 'undescribed';
  const state = meta.grantability;
  switch (state) {
    case 'grantable':
      return 'grantable';
    case 'reserved':
      return 'reserved';
    case undefined:
      return 'unstated';
    default:
      return unrecognizedGrantability(state);
  }
}

/**
 * A grantability state this bundle does not know is not grantable.
 *
 * `state: never` is the point — the day the server grows a third grantability
 * this stops compiling and somebody has to decide what a screen should say
 * about it, rather than the new state silently landing in whichever branch an
 * `if/else` happened to end on. `packages/api-client`'s `isGrantable` uses the
 * same device for the same reason, and this file must not be the one place that
 * quietly widens.
 */
function unrecognizedGrantability(state: never): GrantStanding {
  void state;
  return 'unstated';
}

/** Only `grantable` may be offered. The other three are all default-deny. */
export function mayOfferKey(meta: UsersNS.PermissionMeta | undefined): boolean {
  return grantStanding(meta) === 'grantable';
}

/** The badge beside a key that cannot be picked, or `null` when it can. */
export function grantStandingBadge(standing: GrantStanding): string | null {
  switch (standing) {
    case 'grantable':
      return null;
    case 'reserved':
      return 'Reserved';
    case 'undescribed':
      return 'Not described';
    case 'unstated':
      return 'Grantability not stated';
  }
}

/**
 * WHY this key cannot be put in a role, in words an administrator can act on.
 *
 * The reserved branch prefers the SERVER'S OWN `reservedReason` — that sentence
 * is written per key by the people who decided to withhold it, and paraphrasing
 * it here would put a second, vaguer answer in front of the real one. The
 * fallback exists only for a reserved entry that arrived without one, which the
 * server's own convention forbids but a skewed deployment could still produce.
 */
export function grantStandingExplanation(
  standing: GrantStanding,
  meta: UsersNS.PermissionMeta | undefined,
): string | null {
  switch (standing) {
    case 'grantable':
      return null;
    case 'reserved':
      return (
        meta?.reservedReason ??
        'This permission is real and the product declines to hand it out by hand. No administrator can put it in a role or grant it directly; only a platform decision, taken in a migration, can confer it.'
      );
    case 'undescribed':
      return 'This key is in the live catalog and nothing in this product describes it yet. The server refuses to confer a key it cannot describe, so it cannot go in a role — that refusal is deliberate, not a gap.';
    case 'unstated':
      return 'This server did not say whether this permission may be granted by hand. That is not the same as “yes”, so it is not offered here. It usually means the admin app is newer than the API answering it.';
  }
}

/**
 * Every key in the catalog, with the standing the SERVER stated for it.
 *
 * Nothing is dropped. A reserved key stays in the list, visible, unpickable and
 * explained — which is the whole requirement: not an unticked box, and not a
 * hidden row.
 */
export interface KeyStanding {
  readonly key: string;
  readonly name: string;
  readonly module: string;
  readonly standing: GrantStanding;
  /** The key's own `warning`, on the `high` keys that carry one. */
  readonly warning: string | null;
  readonly explanation: string | null;
  readonly meta: UsersNS.PermissionMeta | undefined;
}

export function keyStandings(overview: UsersNS.RolesOverview): KeyStanding[] {
  return overview.permissions.map((row) => {
    const meta = overview.metadata[row.key];
    const standing = grantStanding(meta);
    return {
      key: row.key,
      // The human name when there is one; the raw key when there is not. Never
      // a prettified guess at the key — a fabricated name for an undescribed
      // permission is the same lie as a fabricated grantability.
      name: meta?.name ?? row.key,
      module: meta?.module ?? 'Other',
      standing,
      warning: meta?.warning ?? null,
      explanation: grantStandingExplanation(standing, meta),
      meta,
    };
  });
}

/**
 * How many of the catalog's keys the product declines to hand out, by kind.
 *
 * Rendered above the picker so a reader can see that the unpickable rows are a
 * stated policy with a size, rather than a handful of controls that look broken.
 */
export interface StandingCensus {
  readonly grantable: number;
  readonly reserved: number;
  readonly undescribed: number;
  readonly unstated: number;
}

export function standingCensus(standings: readonly KeyStanding[]): StandingCensus {
  const census = { grantable: 0, reserved: 0, undescribed: 0, unstated: 0 };
  for (const entry of standings) census[entry.standing] += 1;
  return census;
}
